import { useState } from 'react';
import { Button, Flex, Menu, Modal, Textarea } from '@mantine/core';
import { BetaIcon, BetaText } from '../../../../lib/components';
import { FormProvider, useForm, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import classes from './RiskAssessmentStatus.module.css';
import * as yup from 'yup';

import useValidatedSearchParams from '../../../router/useValidatedSearchParams';
import { SupplierRiskAssessmentResponse } from '../../../hooks';
import { PlotRiskAssessmentResponse } from '../../../hooks/usePlotRiskAssessments';

export type RiskAssessmentFormData<T> = {
  notes: string;
  status: T;
};

const riskAssesmentSchema = yup.object().shape({
  notes: yup.string(),
  status: yup.string(),
});

type RiskAssessmentProps<T> = {
  assessment:
    | SupplierRiskAssessmentResponse
    | PlotRiskAssessmentResponse
    | undefined;
  onSubmit: (data: RiskAssessmentFormData<T>) => void;
  options: T[];
  loading?: boolean;
};

const RiskAssessment = <T extends string>({
  assessment,
  onSubmit,
  options,
  loading,
}: RiskAssessmentProps<T>) => {
  const { t } = useTranslation();
  const {
    validatedParams,
    SEARCH_PARAMS_KEYS: { RISK_MODAL },
    setValidatedParam,
  } = useValidatedSearchParams();

  const [menuOpened, setMenuOpened] = useState<boolean>(false);

  const modalOpened = validatedParams[RISK_MODAL] === 'open';

  const values = {
    notes: assessment?.notes ?? '',
    status: (assessment?.status ?? 'NEW') as T,
  };

  const form = useForm<RiskAssessmentFormData<T>>({
    resolver: yupResolver(riskAssesmentSchema),
    values,
  });

  const toggleModal = (open: boolean) => {
    setValidatedParam(RISK_MODAL, open ? 'open' : null);
    // reset notes if the status has changed
    const formValues = form.getValues();
    if (assessment?.status !== formValues.status) {
      form.setValue('notes', '');
    }
  };

  const handleCancel = () => {
    toggleModal(false);
    form.reset(values);
  };

  const formValues = form.watch();

  return (
    <FormProvider {...form}>
      <Menu
        opened={menuOpened}
        onChange={setMenuOpened}
        offset={15}
        position="bottom-end"
      >
        <Menu.Target>
          <Flex justify="space-between" style={{ cursor: 'pointer' }}>
            <BetaText>
              {t(`suppliers.riskAssessment.status.${formValues.status}`)}
            </BetaText>

            <BetaIcon icon="chevron-down" />
          </Flex>
        </Menu.Target>
        <Menu.Dropdown bg="dark.9">
          {options.map((status) => (
            <Menu.Item
              key={status}
              className={classes.menuItem}
              onClick={() => {
                form.setValue(
                  'status' as Path<RiskAssessmentFormData<T>>,
                  status,
                  { shouldDirty: true }
                );
                toggleModal(true);
              }}
            >
              {t(`suppliers.riskAssessment.status.${status}`)}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      <Modal
        centered
        opened={modalOpened}
        onClose={handleCancel}
        title={t('suppliers.riskAssessment.changeStatusWithValue', {
          status: t(`suppliers.riskAssessment.status.${formValues.status}`),
        })}
      >
        <Textarea
          label={t('suppliers.riskAssessment.notes')}
          placeholder={t<string>('suppliers.riskAssessment.clickToAddNote')}
          variant="outline"
          {...form.register('notes')}
          styles={{
            input: {
              border: '1px solid white',
            },
          }}
        />
        <Flex justify="flex-end" gap={10} mt="md">
          <Button
            radius="md"
            variant="outline"
            type="button"
            onClick={handleCancel}
          >
            {t('common.cancel')}
          </Button>
          <Button
            radius="md"
            variant="outline"
            type="submit"
            onClick={form.handleSubmit(onSubmit)}
            loading={loading}
            disabled={!form.formState.isDirty}
          >
            {t('suppliers.riskAssessment.changeStatus')}
          </Button>
        </Flex>
      </Modal>
    </FormProvider>
  );
};

export default RiskAssessment;
